import React, { useMemo, useState } from "react"
import { ClipboardList } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { CrewMemberTacho, LegalCounters } from "@/reducers/tachosSlice"
import { LegalCountersPanel } from "./LegalCountersPanel"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import { getIntlLocale } from "@/lib/i18n/format"

type Card = {
  key: string
  name: string
  counters: LegalCounters
}

// One tab per card: a solo driver's own panel, or each member of a crew
// (the merged crew tacho has no panel of its own - the counters are per
// card, and the dispatcher's telematics shows them the same way).
const cardsOf = (
  t: TFunction,
  driverName: string | null | undefined,
  legalCounters: LegalCounters | null | undefined,
  crew: CrewMemberTacho[] | undefined,
): Card[] => {
  const members = (crew ?? []).flatMap((member, index) =>
    member.legalCounters
      ? [
          {
            key: member.driverId ?? `member-${index}`,
            name:
              member.driverName ??
              member.driverId ??
              t("reports.legalCounters.driverNumber", { number: index + 1 }),
            counters: member.legalCounters,
          },
        ]
      : [],
  )
  if (members.length > 0) return members
  if (legalCounters) {
    return [
      {
        key: "own",
        name: driverName ?? t("reports.legalCounters.driver"),
        counters: legalCounters,
      },
    ]
  }
  return []
}

const formatReadAt = (timestamp: string | null | undefined): string | null => {
  if (!timestamp) return null
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString(getIntlLocale("pl-PL"))
}

export const LegalCountersDialog: React.FC<{
  driverName?: string | null
  legalCounters?: LegalCounters | null
  crew?: CrewMemberTacho[]
  timestamp?: string | null
}> = ({ driverName, legalCounters, crew, timestamp }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const cards = useMemo(
    () => cardsOf(t, driverName, legalCounters, crew),
    [t, driverName, legalCounters, crew],
  )
  if (cards.length === 0) return null
  const readAt = formatReadAt(timestamp)
  const cardLabel =
    cards.length === 1
      ? cards[0].name
      : t("reports.legalCounters.perDriverCard")

  return (
    <>
      <Button
        className="h-8 w-8"
        variant="outline"
        onClick={() => setOpen(true)}
        title={t("tacho.legalCountersButton")}
        aria-label={t("tacho.legalCountersButton")}
      >
        <ClipboardList className="h-3 w-3" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("reports.legalCounters.title")}</DialogTitle>
            <DialogDescription>
              {readAt
                ? t("reports.legalCounters.descriptionReadAt", {
                    name: cardLabel,
                    at: readAt,
                  })
                : cardLabel}
            </DialogDescription>
          </DialogHeader>
          {cards.length === 1 ? (
            <LegalCountersPanel counters={cards[0].counters} />
          ) : (
            <Tabs defaultValue={cards[0].key}>
              <TabsList>
                {cards.map((card) => (
                  <TabsTrigger key={card.key} value={card.key}>
                    {card.name}
                  </TabsTrigger>
                ))}
              </TabsList>
              {cards.map((card) => (
                <TabsContent key={card.key} value={card.key}>
                  <LegalCountersPanel counters={card.counters} />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export default LegalCountersDialog
